import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { userService } from '../../services/userService';
import { showToast } from '../../utils/toast';

// Async thunks
export const getUserProfile = createAsyncThunk(
  'user/getProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await userService.getProfile();
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to fetch profile';
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateUserProfile = createAsyncThunk(
  'user/updateProfile',
  async (profileData, { rejectWithValue }) => {
    try {
      const response = await userService.updateProfile(profileData);
      showToast.success('Profile updated successfully!', {
        position: 'top-center',
        autoClose: 2000,
      });
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to update profile';
      showToast.error(errorMessage, { position: 'top-center' });
      return rejectWithValue(errorMessage);
    }
  }
);

export const getDashboardInfo = createAsyncThunk(
  'user/getDashboardInfo',
  async (_, { rejectWithValue }) => {
    try {
      const response = await userService.getDashboardInfo();
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to fetch dashboard info';
      return rejectWithValue(errorMessage);
    }
  }
);

export const createSupportTicket = createAsyncThunk(
  'user/createTicket',
  async (ticketData, { rejectWithValue }) => {
    try {
      const response = await userService.createTicket(ticketData);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to create support ticket';
      return rejectWithValue(errorMessage);
    }
  }
);

export const getSupportTickets = createAsyncThunk(
  'user/getTickets',
  async (_, { rejectWithValue }) => {
    try {
      const response = await userService.getTickets();
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to fetch support tickets';
      return rejectWithValue(errorMessage);
    }
  }
);

const initialState = {
  profile: null,
  dashboardInfo: null,
  tickets: [],
  loading: false,
  error: null,
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    clearUserError: (state) => {
      state.error = null;
    },
    clearUserData: (state) => {
      state.profile = null;
      state.dashboardInfo = null;
      state.tickets = [];
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Get Profile
    builder
      .addCase(getUserProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUserProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = action.payload.profile; // Extract profile from response
        state.error = null;
      })
      .addCase(getUserProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Update Profile
    builder
      .addCase(updateUserProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = action.payload.profile; // Extract profile from response
        state.error = null;
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Get Dashboard Info
    builder
      .addCase(getDashboardInfo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getDashboardInfo.fulfilled, (state, action) => {
        state.loading = false;
        state.dashboardInfo = action.payload;
        state.error = null;
      })
      .addCase(getDashboardInfo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Create Support Ticket
    builder
      .addCase(createSupportTicket.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createSupportTicket.fulfilled, (state, action) => {
        state.loading = false;
        // Ticket created successfully, we'll refresh the list instead of trying to add the response
        // The response only contains {message: "Ticket submitted successfully"}
        state.error = null;
      })
      .addCase(createSupportTicket.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Get Support Tickets
    builder
      .addCase(getSupportTickets.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSupportTickets.fulfilled, (state, action) => {
        state.loading = false;
        // Backend returns {tickets: [...]} so we need to extract the tickets array
        state.tickets = action.payload.tickets || [];
        state.error = null;
      })
      .addCase(getSupportTickets.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearUserError, clearUserData } = userSlice.actions;
export default userSlice.reducer; 